import React from "react";
import { useTranslation } from "react-i18next";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Flame } from "lucide-react";

/**
 * BlowCandlesEditor — merchant config for the "Souffle les bougies" game.
 * Lives in the AdminGameEditor alongside NinjaEditor / GuessPriceEditor.
 * It does NOT touch segments (the merchant still edits them through the
 * regular segments panel, same as Wheel / Scratch Card).
 */
export default function BlowCandlesEditor({ game, onGameChange }) {
  const { t } = useTranslation();

  function set(patch) {
    onGameChange(patch);
  }

  return (
    <div className="qrise-card p-6 space-y-5" data-testid="blow-candles-editor">
      <div className="flex items-center gap-2">
        <Flame className="h-4 w-4 text-amber-400" />
        <div className="text-sm font-semibold">
          {t("blow.editor_title", "Configuration du gâteau")}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label className="text-xs font-medium">
            {t("blow.editor_cake_style", "Style du gâteau")}
          </Label>
          <Select
            value={game.cake_style || "classic"}
            onValueChange={(v) => set({ cake_style: v })}
          >
            <SelectTrigger
              className="mt-1.5 rounded-xl h-10"
              data-testid="blow-editor-cake-style"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="classic">
                {t("blow.cake_classic", "Classique à étages")}
              </SelectItem>
              <SelectItem value="cupcake">
                {t("blow.cake_cupcake", "Cupcake")}
              </SelectItem>
              <SelectItem value="macaron">
                {t("blow.cake_macaron", "Macaron géant")}
              </SelectItem>
              <SelectItem value="tiered">
                {t("blow.cake_tiered", "Pièce montée")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs font-medium">
            {t("blow.editor_candles_count", "Nombre de bougies")}
          </Label>
          <Input
            type="number"
            min={1}
            max={10}
            value={game.candles_count ?? 5}
            onChange={(e) =>
              set({
                candles_count: Math.max(1, Math.min(10, Number(e.target.value) || 5)),
              })
            }
            className="mt-1.5 h-10 rounded-xl"
            data-testid="blow-editor-candles-count"
          />
          <p className="mt-1 text-[11px] text-muted-foreground">
            {t("blow.editor_candles_hint", "Entre 1 et 10 bougies.")}
          </p>
        </div>

        <div>
          <Label className="text-xs font-medium">
            {t("blow.editor_difficulty", "Difficulté du souffle")}
          </Label>
          <Select
            value={game.candles_difficulty || "normal"}
            onValueChange={(v) => set({ candles_difficulty: v })}
          >
            <SelectTrigger
              className="mt-1.5 rounded-xl h-10"
              data-testid="blow-editor-difficulty"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="easy">{t("blow.difficulty_easy", "Facile")}</SelectItem>
              <SelectItem value="normal">{t("blow.difficulty_normal", "Normale")}</SelectItem>
              <SelectItem value="hard">{t("blow.difficulty_hard", "Difficile")}</SelectItem>
            </SelectContent>
          </Select>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {t(
              "blow.editor_difficulty_hint",
              "Règle la sensibilité du micro et la vitesse de tap."
            )}
          </p>
        </div>

        <div>
          <Label className="text-xs font-medium">
            {t("blow.editor_max_duration", "Durée maximale (sec)")}
          </Label>
          <Input
            type="number"
            min={5}
            max={60}
            value={game.candles_max_duration ?? 20}
            onChange={(e) =>
              set({
                candles_max_duration: Math.max(
                  5,
                  Math.min(60, Number(e.target.value) || 20)
                ),
              })
            }
            className="mt-1.5 h-10 rounded-xl"
            data-testid="blow-editor-max-duration"
          />
        </div>
      </div>
    </div>
  );
}
